import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { AnalysisData } from "../../types";
import { AppSelect, type AppSelectOption } from "../AppSelect";
import { computeTemperatureDomain } from "../../lib/chartUtils";
import { SAFE_TEMPERATURE_RANGE, TEMPERATURE_COLOR_PALETTE } from "../../lib/temperature";
import { fetchJsonWithPerf, logFrontEvent, type FrontPerfTrace } from "../../lib/perfAudit";
import type { DatasetDateRange } from "../../lib/datasetDateRange";
import type { AnalysisFilters } from "./types";
import { AnalysisChart } from "./AnalysisChart";
import { AnalysisTable } from "./AnalysisTable";
import {
  buildMonthFilterOptions,
  buildMonthFilterOptionsFromValues,
  buildSeriesRecords,
  filterRecords,
  filterRecordsByExcursion,
  filterRecordsByShipmentMonth,
  type MonthFilterMode,
} from "./analysisModel";

type AnalysisPanelProps = {
  clientId: string;
  datasetId: string;
  initialFilters: AnalysisFilters;
  perfTrace?: FrontPerfTrace | null;
  colorPalette?: readonly string[];
  safeRange?: { min: number; max: number };
  yAxisDomain?: readonly [number, number] | null;
  hasCity?: boolean | null;
  hasBoxes?: boolean;
  dateRange?: DatasetDateRange | null;
};

type AnalysisPagePayload = {
  analysis: AnalysisData;
  paging: {
    offset: number;
    limit: number;
    returned: number;
    total: number;
    unfilteredTotal?: number;
    hasMore: boolean;
    nextOffset: number | null;
  };
  facets?: {
    shipmentMonths?: {
      monthYear?: string[];
      month?: string[];
    };
  };
};

const ANALYSIS_PAGE_SIZE = 96;

const MONTH_FILTER_MODE_OPTIONS: AppSelectOption[] = [
  { value: "none", label: "Aucune" },
  { value: "monthYear", label: "Mois + année" },
  { value: "month", label: "Mois (toutes années)" },
];

export function AnalysisPanel({
  clientId,
  datasetId,
  initialFilters,
  perfTrace = null,
  colorPalette = TEMPERATURE_COLOR_PALETTE,
  safeRange = SAFE_TEMPERATURE_RANGE,
  yAxisDomain = null,
  hasCity = null,
  hasBoxes = false,
  dateRange = null,
}: AnalysisPanelProps) {
  const [filters, setFilters] = useState<AnalysisFilters>(initialFilters);
  const [monthFilterMode, setMonthFilterMode] = useState<MonthFilterMode>("none");
  const [selectedMonthFilters, setSelectedMonthFilters] = useState<string[]>([]);
  const [onlyExcursions, setOnlyExcursions] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedSeriesById, setSelectedSeriesById] = useState(() => new Map<string, AnalysisData["series"][number]>());
  const showCityColumn = hasCity !== false;
  const showBoxesColumn = hasBoxes;
  const queryFilters = useMemo(
    () => ({
      filters,
      monthFilterMode,
      selectedMonthFilters,
      onlyExcursions,
      dateRange,
    }),
    [dateRange, filters, monthFilterMode, onlyExcursions, selectedMonthFilters],
  );
  const queryKey = useMemo(
    () => ["dataset-analysis-page", clientId, datasetId, pageIndex, ANALYSIS_PAGE_SIZE, queryFilters],
    [clientId, datasetId, pageIndex, queryFilters],
  );

  const analysisQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      fetchAnalysisPage({
        clientId,
        datasetId,
        offset: pageIndex * ANALYSIS_PAGE_SIZE,
        limit: ANALYSIS_PAGE_SIZE,
        filters,
        monthFilterMode,
        selectedMonthFilters,
        onlyExcursions,
        safeRange,
        dateRange,
        signal,
        perfTrace,
    }),
    enabled: Boolean(clientId && datasetId),
    staleTime: 5 * 60_000,
    gcTime: 20 * 60_000,
    placeholderData: (previous) => previous,
  });

  const series = analysisQuery.data?.analysis.series ?? [];
  const filteredCount = analysisQuery.data?.paging.total ?? 0;
  const totalCount = analysisQuery.data?.paging.unfilteredTotal ?? filteredCount;

  const seriesRecords = useMemo(() => buildSeriesRecords(series, safeRange), [series, safeRange]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const excursionFilteredRecords = useMemo(
    () => filterRecordsByExcursion(seriesRecords, onlyExcursions),
    [seriesRecords, onlyExcursions],
  );
  const monthFilterOptions = useMemo(
    () => {
      if (monthFilterMode === "none") return [];

      const values = analysisQuery.data?.facets?.shipmentMonths?.[monthFilterMode];
      if (values) {
        return buildMonthFilterOptionsFromValues(values, monthFilterMode);
      }

      return buildMonthFilterOptions(excursionFilteredRecords, monthFilterMode);
    },
    [analysisQuery.data?.facets?.shipmentMonths, excursionFilteredRecords, monthFilterMode],
  );
  const monthFilteredRecords = useMemo(
    () => filterRecordsByShipmentMonth(excursionFilteredRecords, monthFilterMode, selectedMonthFilters),
    [excursionFilteredRecords, monthFilterMode, selectedMonthFilters],
  );
  const filteredRecords = useMemo(
    () => filterRecords(monthFilteredRecords, filters, showCityColumn, showBoxesColumn),
    [monthFilteredRecords, filters, showCityColumn, showBoxesColumn],
  );
  const filteredSeries = useMemo(() => filteredRecords.map((record) => record.item), [filteredRecords]);
  const filteredSeriesIds = useMemo(() => filteredRecords.map((record) => record.key), [filteredRecords]);
  const monthFilteredSeriesIds = useMemo(
    () => monthFilteredRecords.map((record) => record.key),
    [monthFilteredRecords],
  );
  const selectedSeries = useMemo(() => {
    const records = selectedIds
      .map((id) => selectedSeriesById.get(id))
      .filter((item): item is AnalysisData["series"][number] => Boolean(item));
    return records;
  }, [selectedIds, selectedSeriesById]);

  const allFilteredSelected =
    filteredSeriesIds.length > 0 && filteredSeriesIds.every((id) => selectedIdSet.has(id));
  const someFilteredSelected = !allFilteredSelected && filteredSeriesIds.some((id) => selectedIdSet.has(id));

  const autoDomain = useMemo(() => computeTemperatureDomain(selectedSeries, safeRange), [selectedSeries, safeRange]);
  const valueDomain = yAxisDomain ?? autoDomain;

  useEffect(() => {
    setSelectedIds([]);
    setSelectedSeriesById(new Map());
    setPageIndex(0);
  }, [clientId, datasetId]);

  useEffect(() => {
    setPageIndex(0);
  }, [queryFilters]);

  useEffect(() => {
    setSelectedIds([]);
    setSelectedSeriesById(new Map());
  }, [dateRange]);

  useEffect(() => {
    setSelectedSeriesById((previous) => {
      const next = new Map(previous);
      for (const item of series) {
        next.set(`${item.shipmentId}::${item.deviceId}`, item);
      }
      return next;
    });
  }, [series]);

  useEffect(() => {
    if (monthFilterMode === "none") {
      setSelectedMonthFilters((previous) => (previous.length ? [] : previous));
      return;
    }

    const available = new Set(monthFilterOptions.map((option) => option.value));
    setSelectedMonthFilters((previous) => {
      const next = previous.filter((value) => available.has(value));
      return next.length === previous.length ? previous : next;
    });
  }, [monthFilterMode, monthFilterOptions]);

  const handleToggleSeries = (key: string) => {
    setSelectedIds((prev) => (prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]));
  };
  const handleToggleMonthFilter = useCallback((value: string) => {
    setSelectedMonthFilters((previous) =>
      previous.includes(value) ? previous.filter((item) => item !== value) : [...previous, value],
    );
  }, []);
  const handleSelectAllMonths = () => {
    setSelectedMonthFilters(monthFilterOptions.map((option) => option.value));
  };
  const handleClearMonthFilters = () => {
    setSelectedMonthFilters([]);
  };
  const handleToggleAllFiltered = (checked: boolean) => {
    if (!filteredSeriesIds.length) return;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        filteredSeriesIds.forEach((id) => next.add(id));
      } else {
        filteredSeriesIds.forEach((id) => next.delete(id));
      }
      return Array.from(next.values());
    });
  };

  return (
    <div className="analysis-layout">
      <div className="analysis-controls">
        <div className="analysis-control">
          <label className="analysis-toggle" htmlFor="analysis-only-excursions">
            <input
              id="analysis-only-excursions"
              type="checkbox"
              checked={onlyExcursions}
              onChange={(event) => setOnlyExcursions(event.target.checked)}
            />
            <span>Seulement les excursions</span>
          </label>
        </div>
        <div className="analysis-control">
          <label htmlFor="analysis-month-filter-mode">Filtrer par mois</label>
          <AppSelect
            id="analysis-month-filter-mode"
            value={monthFilterMode}
            options={MONTH_FILTER_MODE_OPTIONS}
            onValueChange={(value) => setMonthFilterMode(value as MonthFilterMode)}
          />
          {monthFilterMode !== "none" ? (
            <div className="analysis-month-picker">
              <div className="analysis-month-actions">
                <button
                  type="button"
                  onClick={handleSelectAllMonths}
                  disabled={!monthFilterOptions.length || selectedMonthFilters.length === monthFilterOptions.length}
                >
                  Tout selectionner
                </button>
                <button type="button" onClick={handleClearMonthFilters} disabled={!selectedMonthFilters.length}>
                  Tout effacer
                </button>
              </div>
              <div className="analysis-month-chips">
                {monthFilterOptions.map((option) => {
                  const selected = selectedMonthFilters.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`analysis-month-chip${selected ? " is-selected" : ""}`}
                      onClick={() => handleToggleMonthFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {analysisQuery.error ? (
        <p className="dd-inline-error">{(analysisQuery.error as Error).message}</p>
      ) : null}

      <div className="analysis-chart chart-panel">
        <h3>Comparative Analysis</h3>
        <AnalysisChart
          series={selectedSeries}
          valueDomain={valueDomain}
          colorPalette={colorPalette}
          safeRange={safeRange}
        />
      </div>

      <AnalysisTable
        series={filteredSeries}
        filters={filters}
        onFiltersChange={setFilters}
        selectedIds={selectedIdSet}
        onToggle={handleToggleSeries}
        allFilteredSelected={allFilteredSelected}
        someFilteredSelected={someFilteredSelected}
        onToggleAllFiltered={handleToggleAllFiltered}
        hasCity={hasCity}
        hasBoxes={hasBoxes}
        pageIndex={pageIndex}
        pageSize={ANALYSIS_PAGE_SIZE}
        filteredCount={filteredCount}
        totalCount={totalCount}
        isLoading={analysisQuery.isFetching}
        onPageChange={setPageIndex}
      />
    </div>
  );
}

async function fetchAnalysisPage(args: {
  clientId: string;
  datasetId: string;
  offset: number;
  limit: number;
  filters: AnalysisFilters;
  monthFilterMode: MonthFilterMode;
  selectedMonthFilters: string[];
  onlyExcursions: boolean;
  safeRange: { min: number; max: number };
  dateRange: DatasetDateRange | null;
  signal: AbortSignal;
  perfTrace: FrontPerfTrace | null;
}): Promise<AnalysisPagePayload> {
  const url = buildAnalysisPageUrl(args);
  const startedAt = performance.now();
  const { data } = await fetchJsonWithPerf<AnalysisPagePayload>(
    url,
    { signal: args.signal },
    {
      label: "query.dataset_analysis_page",
      queryKey: ["dataset-analysis-page", args.clientId, args.datasetId, args.offset, args.limit],
      trace: args.perfTrace,
    },
  );
  const series = data.analysis?.series ?? [];
  const pointCount = series.reduce((sum, item) => sum + item.points.length, 0);
  logFrontEvent("ANALYSIS_PAGE_READY", {
    traceId: args.perfTrace?.id,
    clientId: args.clientId,
    datasetId: args.datasetId,
    offset: args.offset,
    limit: args.limit,
    total: data.paging?.total,
    returned: series.length,
    pointCount,
    durationMs: Number((performance.now() - startedAt).toFixed(2)),
  });
  return data;
}

function buildAnalysisPageUrl(args: {
  clientId: string;
  datasetId: string;
  offset: number;
  limit: number;
  filters: AnalysisFilters;
  monthFilterMode: MonthFilterMode;
  selectedMonthFilters: string[];
  onlyExcursions: boolean;
  safeRange: { min: number; max: number };
  dateRange: DatasetDateRange | null;
}) {
  const params = new URLSearchParams({
    offset: String(args.offset),
    limit: String(args.limit),
  });
  appendParam(params, "shipmentId", args.filters.shipmentId);
  appendParam(params, "deviceId", args.filters.deviceId);
  appendParam(params, "position", args.filters.position);
  appendParam(params, "destinationCity", args.filters.destinationCity);
  appendParam(params, "destinationCountry", args.filters.destinationCountry);
  appendParam(params, "boxIdentifier", args.filters.boxIdentifier);
  if (args.onlyExcursions) {
    params.set("onlyExcursions", "1");
    params.set("temperatureMin", String(args.safeRange.min));
    params.set("temperatureMax", String(args.safeRange.max));
  }
  if (args.monthFilterMode !== "none" && args.selectedMonthFilters.length > 0) {
    params.set("monthMode", args.monthFilterMode);
    params.set("monthValues", args.selectedMonthFilters.join(","));
  }
  if (args.dateRange) {
    params.set("startDate", args.dateRange.start);
    params.set("endDate", args.dateRange.end);
  }

  return `/api/clients/${encodeURIComponent(args.clientId)}/datasets/${encodeURIComponent(args.datasetId)}/analysis?${params.toString()}`;
}

function appendParam(params: URLSearchParams, name: string, value: string) {
  const trimmed = value.trim();
  if (!trimmed) return;
  params.set(name, trimmed);
}
